import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, Upload, ImageIcon } from "lucide-react";

// Stabiele keys — voorkomen dat de review-lijst state-lekt bij herordenen.
const locKey = (l, i) => `${l?.lrk_number || "no-lrk"}-${i}`;
const grpKey = (g, i, j) => `loc${i}-${g?.name || "no-name"}-${j}`;

export default function Step4Controle({
  locaties, aantal, totalGroepen,
  logoUrl, logoUploading, onLogoUpload,
  onBack, onSubmit, submitting,
}) {
  return (
    <Card className="p-6 space-y-4" data-testid="wizard-step-4">
      <h2 className="text-xl font-medium">Controle-overzicht</h2>
      <p className="text-sm text-neutral-600">
        Controleer alle gegevens voordat u indient. U kunt terug om aanpassingen te maken.
      </p>

      {/* Branding — persistente logo-upload */}
      <div className="border rounded-md p-4 bg-neutral-50" data-testid="wizard-branding">
        <div className="flex items-center gap-2 mb-2">
          <ImageIcon className="h-4 w-4 text-neutral-500" />
          <div className="font-medium">Organisatielogo (optioneel)</div>
        </div>
        <p className="text-xs text-neutral-500 mb-3">
          Wordt duurzaam opgeslagen in object-storage — blijft behouden bij deploys en herstarts.
          Ondersteund: PNG, JPEG, WebP, SVG (max 5 MB).
        </p>
        <div className="flex items-center gap-4">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo"
                 className="h-16 w-16 rounded object-contain border bg-white p-1"
                 data-testid="wizard-logo-preview" />
          ) : (
            <div className="h-16 w-16 rounded border border-dashed flex items-center justify-center text-neutral-400 bg-white">
              <ImageIcon className="h-6 w-6" />
            </div>
          )}
          <label className="cursor-pointer">
            <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
                   className="hidden"
                   onChange={e => onLogoUpload(e.target.files?.[0])}
                   data-testid="wizard-logo-input" />
            <span className="inline-flex items-center gap-2 px-3 py-2 rounded-md border bg-white hover:bg-neutral-50 text-sm">
              <Upload className="h-4 w-4" />
              {logoUploading ? "Bezig…" : logoUrl ? "Logo vervangen" : "Logo uploaden"}
            </span>
          </label>
        </div>
      </div>

      <div className="space-y-3">
        {locaties.map((l, i) => {
          const locCap = parseInt(l.capaciteit) || 0;
          const grpSum = (l.groepen || []).reduce((s, g) => s + (parseInt(g.capaciteit) || 0), 0);
          const overCap = locCap > 0 && grpSum > locCap;
          return (
            <div key={locKey(l, i)} className="border rounded-md p-4"
                 data-testid={`wizard-review-loc-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-medium text-lg">
                    {l.name || <span className="text-red-500">— ontbreekt —</span>}
                  </div>
                  <div className="text-xs text-neutral-500">
                    {l.opvangtype} · LRK {l.lrk_number || <span className="text-red-500">?</span>} · {l.capaciteit} plekken
                  </div>
                </div>
                <Badge className="bg-neutral-100 border-none">{l.groepen.length} groepen</Badge>
              </div>
              {overCap && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mb-2"
                     data-testid={`wizard-review-cap-warn-${i}`}>
                  ⚠ Som groepscapaciteiten ({grpSum}) overschrijdt locatie-capaciteit ({locCap})
                </div>
              )}
              <div className="mt-2 space-y-1">
                {l.groepen.map((g, j) => (
                  <div key={grpKey(g, i, j)}
                       className="text-sm flex items-center justify-between border-t border-neutral-100 pt-1">
                    <span>{g.name || <span className="text-red-500">groep zonder naam</span>}</span>
                    <span className="text-xs text-neutral-500">
                      {g.opvangsoort} · {g.leeftijd_min}–{g.leeftijd_max} jr · {g.capaciteit} plekken
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between pt-4 border-t">
        <Button variant="outline" onClick={onBack}>
          <ChevronLeft className="h-4 w-4 mr-1" /> Terug aanpassen
        </Button>
        <Button onClick={onSubmit} disabled={submitting}
                data-testid="wizard-submit"
                style={{ background: "var(--sage)" }} className="text-white">
          {submitting ? "Bezig…" : `Definitief indienen (${aantal} locaties + ${totalGroepen} groepen)`}
        </Button>
      </div>
    </Card>
  );
}
